import { useEffect } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ManagementFlow,
  ManagementGroupPage,
  ManagementMap,
  ManagementStates,
  ManagementDurations,
  ManagementDeadlines,
  ManagementConsultants,
  ManagementRepairMoney,
  ManagementWeeks,
  ManagementExits,
  ManagementPeripherals,
  ManagementTypes,
} from '@evogard/contract';
import {
  loadFailureMessage,
  type ClaimFlowPeriod,
  type ManagementLine,
} from '@evogard/domain';
import { api } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { onClaimChanged } from '../claim/useDesk.js';

/**
 * A conversa da Gestão com a API (`/claims/management`, 23/10/2026).
 *
 * As chaves começam com `sinistros`, o id do módulo no menu: reabrir um
 * encerrado invalida `['sinistros']` inteiro, e o mapa recarrega junto. Todo
 * ato da mesa que muda onde o sinistro está (`onClaimChanged`) também.
 */

const ROOT = ['sinistros', 'gestao'] as const;

function useTenantId() {
  const { data: context } = useSessionContext();
  return context?.currentTenant.id;
}

/** O que a pessoa pode abrir a partir do mapa — a mesa cobra o recurso de cada ato. */
export function useManagementPermissions() {
  const { data: context } = useSessionContext();
  return {
    canOpenClaim: can(context, 'claims.notices', 'ver'),
    canOpenPeripheral: can(context, 'claims.peripherals', 'ver'),
    canSeeClosures: can(context, 'claims.closures', 'ver'),
  };
}

/** O mapa inteiro. A busca marca quem casa, sem tirar ninguém do mapa. */
export function useManagementMap(search: string) {
  const tenantId = useTenantId();
  const client = useQueryClient();
  useEffect(
    () => onClaimChanged(() => void client.invalidateQueries({ queryKey: ROOT })),
    [client],
  );
  return useQuery({
    queryKey: [...ROOT, 'mapa', tenantId, search],
    queryFn: async (): Promise<ManagementMap> => {
      const r = await api.claimManagement.managementMap({
        query: search ? { search } : {},
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o mapa da gestão'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    /* A busca troca a chave: o mapa continua de pé enquanto a resposta nova não chega. */
    placeholderData: keepPreviousData,
  });
}

/** Um ponto do trilho: a etapa inteira, a estação (`estacao`) ou um marco (a situação). */
export interface ManagementPoint {
  line: ManagementLine;
  phase: string;
  step?: string;
}

export type ManagementOnly = 'sla_expirado' | 'terceiro' | 'parado';

/** A lista de um ponto do trilho, do mais antigo na fase ao mais novo. */
export function useManagementGroup(
  point: ManagementPoint | null,
  filter: { only?: ManagementOnly; search?: string },
  page: number,
  perPage: number,
) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'ponto', tenantId, point, filter, page, perPage],
    queryFn: async (): Promise<ManagementGroupPage> => {
      const r = await api.claimManagement.managementGroup({
        query: {
          line: point!.line,
          phase: point!.phase,
          page,
          perPage,
          ...(point!.step ? { step: point!.step } : {}),
          ...(filter.only ? { only: filter.only } : {}),
          ...(filter.search ? { search: filter.search } : {}),
        },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os acionamentos'));
      return r.body;
    },
    enabled: point !== null && tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** O fluxo do sinistro do Painel da Gestão do sinistro: os abertos nos últimos `days` dias. */
export function useManagementFlow(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  const client = useQueryClient();
  useEffect(
    () => onClaimChanged(() => void client.invalidateQueries({ queryKey: ROOT })),
    [client],
  );
  return useQuery({
    queryKey: [...ROOT, 'fluxo', tenantId, days],
    queryFn: async (): Promise<ManagementFlow> => {
      const r = await api.claimManagement.managementFlow({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o fluxo do sinistro'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** Os sinistros por estado do Painel da Gestão do sinistro: a mesma coorte do fluxo. */
export function useManagementStates(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'estados', tenantId, days],
    queryFn: async (): Promise<ManagementStates> => {
      const r = await api.claimManagement.managementStates({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os sinistros por estado'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** Os tipos de sinistro do Painel da Gestão do sinistro: a mesma coorte do mapa por estado. */
export function useManagementTypes(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'tipos', tenantId, days],
    queryFn: async (): Promise<ManagementTypes> => {
      const r = await api.claimManagement.managementTypes({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os tipos de sinistro'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** O tempo por departamento do Painel da Gestão do sinistro: a mesma coorte do fluxo. */
export function useManagementDurations(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'tempos', tenantId, days],
    queryFn: async (): Promise<ManagementDurations> => {
      const r = await api.claimManagement.managementDurations({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o tempo por departamento'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** O dinheiro do Reparo do Painel da Gestão do sinistro: o que foi LANÇADO no período. */
export function useManagementRepairMoney(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'dinheiro-reparo', tenantId, days],
    queryFn: async (): Promise<ManagementRepairMoney> => {
      const r = await api.claimManagement.managementRepairMoney({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o dinheiro do Reparo'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** Os sinistros por consultor do Painel da Gestão do sinistro: a mesma coorte do mapa. */
export function useManagementConsultants(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'consultores', tenantId, days],
    queryFn: async (): Promise<ManagementConsultants> => {
      const r = await api.claimManagement.managementConsultants({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os sinistros por consultor'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** O prazo por etapa do Painel da Gestão do sinistro: a mesma coorte do fluxo. */
export function useManagementDeadlines(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'prazos', tenantId, days],
    queryFn: async (): Promise<ManagementDeadlines> => {
      const r = await api.claimManagement.managementDeadlines({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o prazo por etapa'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** Os avisos por semana do Painel da Gestão do sinistro: a mesma coorte do fluxo. */
export function useManagementWeeks(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'semanas', tenantId, days],
    queryFn: async (): Promise<ManagementWeeks> => {
      const r = await api.claimManagement.managementWeeks({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os avisos por semana'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** As negativas e desistências do Painel da Gestão do sinistro: a mesma coorte do fluxo. */
export function useManagementExits(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'saidas', tenantId, days],
    queryFn: async (): Promise<ManagementExits> => {
      const r = await api.claimManagement.managementExits({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'as negativas e desistências'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/**
 * O Painel da Gestão dos periféricos (28/10/2026): uma resposta só, para todos
 * os gráficos — o mesmo período vale para todos, e a API conta tudo de uma vez.
 */
export function useManagementPeripherals(days: ClaimFlowPeriod) {
  const tenantId = useTenantId();
  const client = useQueryClient();
  useEffect(
    () => onClaimChanged(() => void client.invalidateQueries({ queryKey: ROOT })),
    [client],
  );
  return useQuery({
    queryKey: [...ROOT, 'perifericos', tenantId, days],
    queryFn: async (): Promise<ManagementPeripherals> => {
      const r = await api.claimManagement.managementPeripherals({ query: { days } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o painel dos periféricos'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: keepPreviousData,
  });
}
